import i18n from "../service/i18n";

import { isAreenaEmbed, isYleEmbed } from "./commonUtils";
import { encodeParams } from "./url";

type UrlParams = Record<string, string | boolean | number>;

const areenaOnDemandUrl = (programId: string, language: string) =>
  `${i18n("areenaUrl", { language })}/${programId}`;

/**
 * Get link to the areena recommendation with possible utm params
 */
export const getRecommendationUrl = ({
  autoplay = false,
  index,
  language,
  programId
}: {
  autoplay?: boolean;
  index: number;
  language: string;
  programId: string;
}) => {
  const params: UrlParams = isYleEmbed()
    ? {}
    : {
        utm_campaign: "player-recommendations",
        utm_content: `yle_referrer:${programId}|place:${index}`,
        utm_source: window.location.host
      };
  if (autoplay) {
    params["autoplay"] = true;
  }

  return getAreenaUrl({
    language,
    params,
    programId
  });
};

/**
 * Get link that can be used on social media share buttons etc.
 * Adds required utm query params
 * If socialMediaUrl is given, link will redirect to that social media share page
 */
export const getShareUrl = ({
  isSimulcast = false,
  language,
  programId,
  seek,
  socialMediaUrl,
  utmSource
}: {
  isSimulcast?: boolean;
  language: string;
  programId: string;
  seek?: number;
  socialMediaUrl?: string;
  utmSource: string;
}) => {
  const params: Record<string, string> = {
    utm_campaign: isAreenaEmbed() ? "areena-web-share" : "player-web-share",
    utm_medium: "social",
    utm_source: utmSource
  };
  if (seek !== undefined) {
    params["seek"] = seek.toString();
  }

  const areenaLink = getAreenaUrl({
    isSimulcast,
    language,
    params,
    programId
  });

  if (socialMediaUrl) {
    return `${socialMediaUrl}${encodeURIComponent(areenaLink)}`;
  }

  return areenaLink;
};

/**
 * Get link to the program page on areena
 * Simulcast videos do not have own pages, so direct to "suorat" -page
 */
export const getAreenaUrl = ({
  isSimulcast = false,
  language,
  params = {},
  programId
}: {
  isSimulcast?: boolean;
  language: string;
  params?: UrlParams;
  programId: string;
}) => {
  const queryString = encodeParams(params, false);
  const programUrl = isSimulcast
    ? `${i18n("areenaSimulcastUrl", { language })}`
    : areenaOnDemandUrl(programId, language);

  const queryPrefix = queryString ? (programUrl.includes("?") ? "&" : "?") : "";

  return `${programUrl}${queryPrefix}${queryString}`;
};
